import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { LoginService } from 'src/app/pages/login/services/login.service';
import { TipoDocumento } from 'src/app/pages/configuracion/Envios/documentos/models/tipo-documento';
import { normalizarDocumentoEntradaApi } from 'src/app/pages/configuracion/Envios/documentos/services/documento-entrada-api-normalizer';
import { ConversionDatosApiService } from 'src/app/shared/services/conversion-datos-api.service';
import { EndpointConfigV3 } from 'src/environments/Configuration/EnpointConfig.v3';
import { environment } from 'src/environments/environment';
import { EndpointService } from 'src/app/shared/services/endpoint.service';

export interface CatalogoGenericoItem {
  id: number;
  nombre: string;
}

export interface DocumentoGenericoEnvioSimpleResponse {
  urlPdf: string;
  configuracion: TipoDocumento;
}

interface CatalogoGenericoApiItem {
  Id: number;
  Nombre: string;
}

interface DocumentoGenericoEnvioSimpleApiResponse {
  UrlPdf: string;
  Configuracion: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class EnvioSimpleDocumentoApiService {
  /**
   * Catalogo ligero via `GET GetCatalogo?generico=true`.
   * Configuracion completa via `GET GetDocumentoGenericoEnvioSimple/{idTipoDocumento}`.
   */

  /** Cache del catalogo (id + nombre) durante la sesion del paso. */
  private catalogoCache: CatalogoGenericoItem[] | null = null;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private endpointService: EndpointService,
    private conversionDatosApiService: ConversionDatosApiService
  ) {}

  getCatalogoGenericos(): Observable<CatalogoGenericoItem[]> {
    if (this.catalogoCache !== null) {
      return of(this.catalogoCache);
    }
    const options = {
      headers: this.buildAuthHeaders(),
      params: new HttpParams().set('generico', 'true')
    };
    return this.http.get<unknown>(this.resolveGetCatalogoUrl(), options).pipe(
      map(response => this.mapCatalogoApiResponse(response)),
      tap(catalogo => (this.catalogoCache = catalogo))
    );
  }

  getDocumentoGenerico(idTipoDocumento: number | string): Observable<DocumentoGenericoEnvioSimpleResponse> {
    const url = this.resolveGetDocumentoGenericoUrl(idTipoDocumento);
    return this.http.get<unknown>(url, { headers: this.buildAuthHeaders() }).pipe(
      map(response => this.mapDocumentoGenericoApiResponse(response))
    );
  }

  /** Limpia la cache del catalogo (p. ej. al reiniciar el flujo). */
  limpiarCacheCatalogo(): void {
    this.catalogoCache = null;
  }

  /**
   * GetCatalogo puede no venir aun en los endpoints del login; en ese caso se usa
   * la ruta de `EndpointConfigV3` (mismo patron que otros endpoints V3 nuevos).
   */
  private resolveGetCatalogoUrl(): string {
    try {
      return this.endpointService.UrlGetCatalogo;
    } catch {
      const config = EndpointConfigV3.find(endpoint => endpoint.nombre === 'GetCatalogo');
      const path = config?.endpointV3 ?? 'api/TipoDocumento/GetCatalogo';
      return `${environment.ApiUrlV3}${path}`;
    }
  }

  private resolveGetDocumentoGenericoUrl(idTipoDocumento: number | string): string {
    const id = String(idTipoDocumento);
    try {
      return this.endpointService.UrlGetDocumentoGenericoEnvioSimple.replace('{idTipoDocumento}', id);
    } catch {
      const config = EndpointConfigV3.find(endpoint => endpoint.nombre === 'GetDocumentoGenericoEnvioSimple');
      const path = (
        config?.endpointV3 ?? 'api/TipoDocumento/GetDocumentoGenericoEnvioSimple/{idTipoDocumento}'
      ).replace('{idTipoDocumento}', id);
      return `${environment.ApiUrlV3}${path}`;
    }
  }

  private buildAuthHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      authorization: 'Bearer ' + this.loginService.getUserLocalStorage().Token.Token
    });
  }

  private mapCatalogoApiResponse(response: unknown): CatalogoGenericoItem[] {
    const items: CatalogoGenericoApiItem[] = Array.isArray(response)
      ? response
      : ((response as { Datos?: CatalogoGenericoApiItem[] })?.Datos ?? []);
    return items.map(item => ({
      id: item.Id,
      nombre: item.Nombre
    }));
  }

  private mapDocumentoGenericoApiResponse(response: unknown): DocumentoGenericoEnvioSimpleResponse {
    const body = this.extractDocumentoGenericoBody(response);
    if (!body?.UrlPdf || !body?.Configuracion) {
      throw new Error('Respuesta de documento generico invalida');
    }
    const configuracion = this.conversionDatosApiService.mapDocumentosEntrada([body.Configuracion])?.[0] as unknown as
      | TipoDocumento
      | undefined;
    if (!configuracion) {
      throw new Error('No se pudo mapear la configuracion del documento generico');
    }
    normalizarDocumentoEntradaApi(configuracion);
    return {
      urlPdf: body.UrlPdf,
      configuracion
    };
  }

  private extractDocumentoGenericoBody(response: unknown): DocumentoGenericoEnvioSimpleApiResponse | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const direct = response as DocumentoGenericoEnvioSimpleApiResponse;
    if (direct.UrlPdf && direct.Configuracion) {
      return direct;
    }
    const wrapped = response as { Datos?: DocumentoGenericoEnvioSimpleApiResponse };
    if (wrapped.Datos?.UrlPdf && wrapped.Datos?.Configuracion) {
      return wrapped.Datos;
    }
    return null;
  }
}
